import { cn } from "@wa/ui";
import { BAND_RING, BAND_TEXT, scoreBand } from "../../../lib/siteScan";

/**
 * The opportunity score as a dial.
 *
 * A number between 0 and 100 means nothing on its own, so the ring carries the
 * band colour and the caption carries the verdict in words. The arc is drawn
 * with a stroke-dashoffset transition rather than an animation library: it is
 * one property, and it stays smooth while the rest of the page is still
 * mounting.
 *
 * The band colours come from `lib/siteScan`, which is the third place they were
 * written down and now the only one. The dial had its own copy carrying a `glow`
 * shadow class alongside them - a class that never did anything, because the
 * element it sat on has no `shadow-*` utility to give it a colour.
 */

export function ScoreDial({
  score,
  label,
  caption,
  size = 132,
}: {
  score: number;
  label: string;
  caption: string;
  size?: number;
}) {
  const band = scoreBand(score);
  const stroke = 9;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - Math.min(100, Math.max(0, score)) / 100);

  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90" role="img" aria-label={`${label} ${score}`}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            strokeWidth={stroke}
            className="stroke-muted"
            strokeOpacity={0.45}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className={cn(BAND_RING[band], "transition-[stroke-dashoffset] duration-1000 ease-out")}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={cn("text-3xl font-bold tabular-nums leading-none", BAND_TEXT[band])}>{score}</span>
          <span className="mt-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            {label}
          </span>
        </div>
      </div>
      <p className={cn("text-center text-xs font-semibold", BAND_TEXT[band])}>{caption}</p>
    </div>
  );
}
